import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  getFileDownloadUrl,
  getFileFromFirebaseStorage,
} from "../../../api/firebase/firestoreService";
import { ToastMessages } from "../../../components/toasts/toastMessages";
import { GeneralOrderDetails } from "../../../data-model/medicalPaOrdersModel";
import { SingleOrderDetailsTableName } from "../../../enums/singleOrderDetailsTableName";
import { UserGeneralInfoContextKeys } from "../../../enums/userGeneralInfoContextKeys";
import { useUpdateOrderDetails } from "../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { resetFileUploadState } from "../../../redux/slice/fileUploadSlice";
import { AppDispatch } from "../../../redux/store/store";
import { UserStore } from "../../../store/UserGeneralInfoStore";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import { mergeDocuments } from "../../../utils/documentMerger";
import { downloadFile } from "../../../utils/fileDownloader";
import { uploadToFirebaseStorage } from "../../../utils/fileUpload";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { getPatientName } from "../utils/documentHelper";
import { generatePathForFileUpload } from "../utils/generatePathForFileUpload";

/**
 * Props for {@link useDocumentManagement}.
 *
 * @property order - Postgres-backed general order details (from
 *   `generalOrderDetails.order`), or null while loading/unavailable.
 * @property isLoadingOrder - Loading flag for the general order details fetch.
 * @property orderId - Route order id; used as the storage/update document id.
 * @property filledPdfPath - Filled fax PDF storage path from
 *   `submission.submission_output.filled_pdf_link`. Previewed when the
 *   navigation-only `location.state.faxPdfLink` is absent (page refresh), so
 *   the packet does not silently fall back to the clinical document.
 */
interface UseDocumentManagementProps {
  order: GeneralOrderDetails | null;
  isLoadingOrder: boolean;
  orderId: string;
  skipDocumentDownload?: boolean;
  filledPdfPath?: string | null;
}

/* In-session cache of resolved Firebase Storage download URLs, keyed by the
 * storage path. Download URLs embed a long-lived token, so reusing them within
 * a session lets a document re-open instantly instead of re-issuing a
 * `getDownloadURL` round-trip on every mount.
 */
const downloadUrlCache = new Map<string, string>();

/**
 * Manages the order's clinical document: resolving its viewable URL, optionally
 * downloading the raw blob for merge/download, and caching resolved URLs for
 * instant re-opens.
 *
 * `hasResolvedDocument` starts false and only flips once the document has been
 * resolved (or the order turned out to have nothing to fetch), so callers can
 * keep showing a loader instead of an empty state during the gap between mount
 * and the first fetch.
 *
 * @param props - Order context plus the optional `skipDocumentDownload` flag
 * @returns Document state and actions (`pdfUrl`, `isLoading`, `hasResolvedDocument`, `error`, `documents`, `downloadPdf`, ...)
 */
export const useDocumentManagement = ({
  order,
  isLoadingOrder,
  orderId,
  skipDocumentDownload = false,
  filledPdfPath = null,
}: UseDocumentManagementProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();

  const orgId = getOrgIdForFetchExternalWorklist();

  const { runUpdate: runClinicalDocumentUpdate } = useUpdateOrderDetails<{
    file_path: string;
  }>({
    orderId,
    orgId: orgId ?? "",
    tableName: SingleOrderDetailsTableName.CLINICAL_DOCUMENT,
  });

  const uuid = useMemo(() => uuidv4(), []);

  const [patientName, setPatientName] = useState<string>("");
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasResolvedDocument, setHasResolvedDocument] =
    useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [documents, setDocuments] = useState<File[]>([]);

  // Last fetched storage path, so re-renders of the same document are skipped
  const previousDocumentPathRef = useRef<string | null>(null);

  /**
   * Persists the freshly uploaded clinical-document path to Postgres via the
   * generic single-order-details update endpoint (`clinical-document` table).
   *
   * @param filePath - Storage path of the merged clinical document
   */
  const updateClinicalDocumentPathInPostgres = useCallback(
    async (filePath: string): Promise<void> => {
      await runClinicalDocumentUpdate({ file_path: filePath });
    },
    [runClinicalDocumentUpdate],
  );

  const uploadDraft = useCallback(
    async (mergedFile: Blob, targetOrderId: string) => {
      const filePath = generatePathForFileUpload(targetOrderId, uuid);

      await uploadToFirebaseStorage({
        file: mergedFile,
        storagePath: filePath,
        fileName: `${uuid}.pdf`,
      });

      await updateClinicalDocumentPathInPostgres(filePath);
    },
    [uuid, updateClinicalDocumentPathInPostgres],
  );

  const fetchDownloadUrl = useCallback(
    async (refUrl: string) => {
      const cachedUrl = downloadUrlCache.get(refUrl);

      // Fast path: URL already resolved and the caller only needs the viewable
      // URL. Serve from cache with no loader flash and no network round-trip.
      if (cachedUrl && skipDocumentDownload) {
        setError("");
        setPdfUrl(cachedUrl);
        setIsLoading(false);
        setHasResolvedDocument(true);
        return;
      }

      setIsLoading(true);
      setError("");
      try {
        const downloadUrl = cachedUrl ?? (await getFileDownloadUrl(refUrl));
        if (downloadUrl) {
          downloadUrlCache.set(refUrl, downloadUrl);
        }
        setPdfUrl(downloadUrl);

        if (skipDocumentDownload) {
          return;
        }

        const downloadedDocument =
          await getFileFromFirebaseStorage(downloadUrl);
        setDocuments(downloadedDocument ? [downloadedDocument] : []);
      } catch (err) {
        logDataToConsole("Error fetching document", err);
        setError("Failed to load the document. Please try again later.");
        setPdfUrl("");
        setDocuments([]);
      } finally {
        setIsLoading(false);
        setHasResolvedDocument(true);
      }
    },
    [skipDocumentDownload],
  );

  /* Storage path of the document to preview, most specific source first: the
   * filled fax PDF handed over by the submit navigation, then the same link
   * re-read from Postgres (survives a refresh), then the clinical document.
   */
  const documentPath =
    location.state?.faxPdfLink ||
    filledPdfPath ||
    order?.clinical_document?.file_path ||
    "";

  useEffect(() => {
    if (!order || isLoadingOrder) {
      return;
    }

    setPatientName(getPatientName(order));

    if (previousDocumentPathRef.current === documentPath) {
      return;
    }
    previousDocumentPathRef.current = documentPath;

    if (!documentPath) {
      setHasResolvedDocument(true);
      return;
    }

    fetchDownloadUrl(documentPath);
  }, [order, isLoadingOrder, documentPath, fetchDownloadUrl]);

  const downloadPdf = useCallback(async () => {
    setIsLoading(true);
    try {
      if (documents && documents.length > 0) {
        const mergedResult = await mergeDocuments(documents, patientName);

        if (!mergedResult) {
          setError("Failed to merge documents. Please try again.");
          toast.error(ToastMessages.DOWNLOAD_FAILURE);
          return;
        }

        await uploadDraft(mergedResult.mergedFile, orderId);

        dispatch(resetFileUploadState());

        await downloadFile({
          file: mergedResult.mergedFile,
          fileName: mergedResult.fileName,
          onSuccess: () => {
            toast.success(ToastMessages.DOWNLOAD_SUCCESS);

            const parentTable =
              UserStore.getItem(UserGeneralInfoContextKeys.PARENT_TABLE) ?? "";
            setTimeout(() => {
              navigate(`/worklists/${parentTable}`);
            }, 200);
          },
          onError: (error) => {
            setError("Failed to download the document. Please try again.");
            toast.error(ToastMessages.DOWNLOAD_FAILURE);
          },
        });
      }
    } catch (err) {
      logError(err as Error, "Error downloading PDF");
      setError("Failed to download the document. Please try again later.");
      toast.error(ToastMessages.DOWNLOAD_FAILURE);
    } finally {
      setIsLoading(false);
    }
  }, [documents, patientName, navigate, dispatch, uploadDraft, orderId]);

  return {
    patientName,
    pdfUrl,
    isLoading,
    hasResolvedDocument,
    error,
    fileNames,
    documents,
    setFileNames,
    setDocuments,
    downloadPdf,
  };
};
